import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { FieldSurveyorOnly } from './FieldSurveyorOnly'
import { createFacility } from '../../lib/services/facilities'
import type { FacilityInput } from '../../lib/services/facilities'

export function FacilityNewPage() {
  const navigate = useNavigate()
  const [form, setForm] = useState<FacilityInput>({
    facility_name: '',
    state: '',
    facility_type: 'ASC',
    address: '',
    city: '',
    zip_code: '',
    license_number: '',
  })
  const [accreditor, setAccreditor] = useState('')
  const [err, setErr] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: createFacility,
    onSuccess: (facility) => navigate(`/facilities/${facility.facility_id}`),
    onError: (e: Error) => setErr(e.message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.facility_name.trim() || !form.state.trim()) {
      setErr('Facility name and state are required.')
      return
    }
    mutation.mutate({
      ...form,
      facility_type: form.facility_type || 'ASC',
      accreditation_framework: accreditor || undefined,
    })
  }

  const set = (field: keyof FacilityInput, value: string) =>
    setForm((f) => ({ ...f, [field]: value }))

  return (
    <FieldSurveyorOnly>
    <FieldAppShell eyebrow="My facilities" title="New facility" backTo="/facilities">
      <div className="space-y-3">
        {err && <p className="rounded-[10px] bg-red-50 px-3 py-2.5 text-[12px] font-bold text-red-700">{err}</p>}

        <div className="fs-surface p-3.5">
          <div className="form-row">
            <span className="fs-label">Facility name *</span>
            <input
              className="fs-form-input"
              placeholder="e.g. Stonebridge Surgical Center"
              value={form.facility_name}
              onChange={(e) => set('facility_name', e.target.value)}
            />
          </div>

          <div className="form-row mt-3">
            <span className="fs-label">Street address</span>
            <input
              className="fs-form-input"
              value={form.address ?? ''}
              onChange={(e) => set('address', e.target.value)}
            />
          </div>

          <div className="mt-3 grid grid-cols-[1fr_70px_90px] gap-2">
            <div>
              <span className="fs-label">City</span>
              <input
                className="fs-form-input mt-1"
                value={form.city ?? ''}
                onChange={(e) => set('city', e.target.value)}
              />
            </div>
            <div>
              <span className="fs-label">State *</span>
              <input
                className="fs-form-input mt-1"
                placeholder="TX"
                value={form.state}
                onChange={(e) => set('state', e.target.value)}
              />
            </div>
            <div>
              <span className="fs-label">ZIP</span>
              <input
                className="fs-form-input mt-1"
                value={form.zip_code ?? ''}
                onChange={(e) => set('zip_code', e.target.value)}
              />
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <span className="fs-label">License #</span>
              <input
                className="fs-form-input mt-1"
                value={form.license_number ?? ''}
                onChange={(e) => set('license_number', e.target.value)}
              />
            </div>
            <div>
              <span className="fs-label">Accreditor</span>
              <select
                className="fs-form-input mt-1"
                value={accreditor}
                onChange={(e) => setAccreditor(e.target.value)}
              >
                <option value="">None / CMS only</option>
                <option value="TJC">TJC</option>
                <option value="AAAHC">AAAHC</option>
                <option value="QUAD A">QUAD A</option>
                <option value="ACHC">ACHC</option>
              </select>
            </div>
          </div>

          <div className="mt-4 border-t pt-3" style={{ borderColor: 'rgba(23,86,141,0.16)' }}>
            <span className="fs-label">Primary contact</span>
          </div>

          <div className="form-row mt-2">
            <span className="fs-label">Contact name</span>
            <input
              className="fs-form-input"
              placeholder="e.g. Jane Smith"
              value={form.contact_name ?? ''}
              onChange={(e) => set('contact_name', e.target.value)}
            />
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <span className="fs-label">Email</span>
              <input
                className="fs-form-input mt-1"
                type="email"
                placeholder="name@facility.com"
                value={form.contact_email ?? ''}
                onChange={(e) => set('contact_email', e.target.value)}
              />
            </div>
            <div>
              <span className="fs-label">Phone</span>
              <input
                className="fs-form-input mt-1"
                type="tel"
                placeholder="(555) 555-1234"
                value={form.contact_phone ?? ''}
                onChange={(e) => set('contact_phone', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate('/facilities')}
            className="inline-flex flex-1 items-center justify-center rounded-[11px] border border-slate-200 bg-white py-2.5 text-[13px] font-bold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit as () => void}
            disabled={mutation.isPending}
            className="fs-btn-primary flex-[2]"
          >
            {mutation.isPending ? 'Creating…' : 'Create facility'}
          </button>
        </div>
      </div>
    </FieldAppShell>
    </FieldSurveyorOnly>
  )
}
